import React from 'react';
import type { GeneratedLocation } from '@bid/shared-types';

interface LocationDisplayProps {
  location: GeneratedLocation;
  onSave?: () => void;
}

export const LocationDisplay: React.FC<LocationDisplayProps> = ({
  location,
  onSave,
}): JSX.Element => {
  const cardStyle: React.CSSProperties = {
    backgroundColor: '#16162a',
    borderRadius: '12px',
    border: '1px solid rgba(212, 175, 55, 0.2)',
    overflow: 'hidden',
  };

  const headerStyle: React.CSSProperties = {
    padding: '24px',
    borderBottom: '1px solid rgba(212, 175, 55, 0.2)',
    background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, transparent 100%)',
  };

  const nameStyle: React.CSSProperties = {
    fontSize: '1.75rem',
    fontWeight: 700,
    color: '#d4af37',
    margin: 0,
    fontFamily: 'Cinzel, serif',
  };

  const contentStyle: React.CSSProperties = {
    padding: '24px',
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: '24px',
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 600,
    color: '#d4af37',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
    marginBottom: '12px',
  };

  const infoGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
  };

  const infoItemStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  };

  const infoLabelStyle: React.CSSProperties = {
    fontSize: '12px',
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  };

  const infoValueStyle: React.CSSProperties = {
    fontSize: '16px',
    color: '#e5e7eb',
    fontWeight: 500,
  };

  const descriptionStyle: React.CSSProperties = {
    fontSize: '15px',
    color: '#9ca3af',
    lineHeight: 1.7,
    fontStyle: 'italic',
    padding: '16px',
    backgroundColor: '#0f0f1e',
    borderRadius: '8px',
    border: '1px solid rgba(212, 175, 55, 0.1)',
  };

  const tagStyle: React.CSSProperties = {
    display: 'inline-block',
    padding: '6px 14px',
    backgroundColor: '#0f0f1e',
    border: '1px solid rgba(212, 175, 55, 0.3)',
    borderRadius: '20px',
    fontSize: '14px',
    color: '#d4af37',
  };

  const saveButtonStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 24px',
    backgroundColor: '#d4af37',
    color: '#0f0f1e',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginTop: '8px',
    boxShadow: '0 4px 12px rgba(212, 175, 55, 0.3)',
  };

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <h2 style={nameStyle}>{location.name}</h2>
      </div>

      <div style={contentStyle}>
        {/* Basic Info */}
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Location Details</h3>
          <div style={infoGridStyle}>
            <div style={infoItemStyle}>
              <span style={infoLabelStyle}>District</span>
              <span style={infoValueStyle}>{location.district}</span>
            </div>
            <div style={infoItemStyle}>
              <span style={infoLabelStyle}>Type</span>
              <span style={infoValueStyle}>{location.buildingType}</span>
            </div>
            <div style={infoItemStyle}>
              <span style={infoLabelStyle}>Security Level</span>
              <span style={infoValueStyle}>{location.securityLevel}/5</span>
            </div>
            <div style={infoItemStyle}>
              <span style={infoLabelStyle}>Haunted</span>
              <span style={infoValueStyle}>{location.haunted ? 'Yes 👻' : 'No'}</span>
            </div>
          </div>
        </div>

        {/* Faction Turf */}
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Faction Turf</h3>
          <span style={tagStyle}>{location.factionTurf}</span>
        </div>

        {/* Description */}
        <div>
          <h3 style={sectionTitleStyle}>Description</h3>
          <p style={descriptionStyle}>{location.description}</p>
        </div>

        {onSave && (
          <button
            onClick={onSave}
            style={saveButtonStyle}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#e5c158';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(212, 175, 55, 0.4)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = '#d4af37';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(212, 175, 55, 0.3)';
            }}
          >
            Save Location
          </button>
        )}
      </div>
    </div>
  );
};
